import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const eur = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  return eur.format(amount);
}

const dateFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/**
 * « 2026-09-14 » en heure locale : la journée du patron, pas celle du serveur.
 * `toISOString()` donne la date UTC, qui change de jour à 2 h du matin l'été.
 */
export function jourLocal(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "—";
  return dateFmt.format(d);
}

/**
 * Un nombre nu, à la française.
 *
 * `formatCurrency` colle un « € » et `formatPercent` un « % » ; il manquait de
 * quoi écrire une heure ou un écart en points. Sans lui, ces chiffres-là
 * sortaient du gabarit anglais de JavaScript — « 61.75 h », « −30.3 pts » —
 * au milieu d'un écran où tous les autres portaient une virgule.
 */
export function formatNumber(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/**
 * Un pourcentage à la française.
 *
 * L'espace avant le signe est **insécable**, et ce n'est pas un raffinement :
 * c'est la règle typographique française, et c'est aussi ce qui empêche
 * « 43,3 % » de se couper en deux lignes dans une colonne étroite. Trouvé sur
 * le tableau de bord de pilotage, où le tableau des marges compte six colonnes
 * sur un tiers d'écran.
 */
export function formatPercent(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits).replace(".", ",")}\u00A0%`;
}

export function clientDisplayName(c: {
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
}): string {
  if (c.company_name) return c.company_name;
  const full = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return full || "Sans nom";
}

/**
 * Combien de jours se sont écoulés depuis cette date.
 *
 * Négatif pour une date à venir. Sert à situer une facture par rapport à son
 * échéance, donc à choisir le palier de relance.
 *
 * Extraite d'`InvoiceDetail` le 12 septembre, quand le tableau de bord a eu
 * besoin du même calcul pour relancer depuis sa propre liste. Deux copies
 * auraient divergé sur l'arrondi, donc sur le palier — et un palier faux
 * envoie la mauvaise lettre au client.
 */
export function daysBetween(from: string): number {
  // `Date.now()` est un instant absolu, `from` une date nue : les comparer
  // directement melait UTC et calendrier local. A 00 h 30 le 15, une facture
  // echue le 14 paraissait encore a jour, parce qu'il etait 22 h 30 UTC le 14.
  // On compare donc deux dates nues, dans le calendrier de celui qui regarde.
  const aujourdhui = Date.parse(`${jourLocal()}T00:00:00Z`);
  const depuis = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  return Math.round((aujourdhui - depuis) / 86_400_000);
}
